import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';

import './input.css';

export interface LovoInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  icon?: ReactNode;
  error?: string;
  density?: 'default' | 'compact';
  showPassword?: boolean;
  onToggleShowPassword?: () => void;
}

export const Input = forwardRef<HTMLInputElement, LovoInputProps>(function Input(
  {
    label,
    icon,
    error,
    density = 'default',
    id,
    className = '',
    type,
    value,
    defaultValue,
    showPassword: controlledShowPassword,
    onToggleShowPassword,
    onBlur,
    onChange,
    onClick,
    onFocus,
    onMouseEnter,
    onMouseLeave,
    ...props
  },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [internalShowPassword, setInternalShowPassword] = useState(false);
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue ?? '');
  const resolvedValue = value ?? uncontrolledValue;
  const filled = String(resolvedValue).length > 0;

  const isPassword = type === 'password';
  const showPassword = controlledShowPassword !== undefined ? controlledShowPassword : internalShowPassword;
  const computedType = isPassword ? (showPassword ? 'text' : 'password') : type;

  const visual = error
    ? { node: '186:39827', variant: 'error' }
    : focused && filled
      ? { node: '186:39807', variant: 'filled+focus' }
      : focused
        ? { node: '186:39789', variant: 'focus' }
        : filled
          ? { node: '186:39817', variant: 'filled' }
          : hovered
            ? { node: '186:39742', variant: 'hover' }
            : { node: '61:27165', variant: 'Default' };
  const classes = [
    'lovo-input',
    `lovo-input--${density}`,
    error ? 'lovo-input--error' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`lovo-input-group lovo-input-group--${density}`}>
      <div
        className={classes}
        data-figma-node={visual.node}
        data-figma-variant={`Property 1=${visual.variant}`}
      >
        {icon ? (
          <div className="lovo-input__icon" aria-hidden="true">
            {icon}
          </div>
        ) : null}
        <label className="lovo-input__field" htmlFor={inputId}>
          <span>{label}</span>
          <input
            {...props}
            defaultValue={value === undefined ? defaultValue : undefined}
            id={inputId}
            onBlur={(event) => {
              setFocused(false);
              onBlur?.(event);
            }}
            onChange={(event) => {
              if (value === undefined) setUncontrolledValue(event.target.value);
              onChange?.(event);
            }}
            onClick={(event) => {
              onClick?.(event);
              if (!event.defaultPrevented && computedType === 'date') {
                try {
                  event.currentTarget.showPicker?.();
                } catch {
                  event.currentTarget.focus();
                }
              }
            }}
            onFocus={(event) => {
              setFocused(true);
              onFocus?.(event);
            }}
            onMouseEnter={(event) => {
              setHovered(true);
              onMouseEnter?.(event);
            }}
            onMouseLeave={(event) => {
              setHovered(false);
              onMouseLeave?.(event);
            }}
            ref={ref}
            type={computedType}
            value={value}
          />
        </label>
        {isPassword && (
          <button
            aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
            className="lovo-input__eye-btn"
            tabIndex={-1}
            type="button"
            onClick={() => {
              if (onToggleShowPassword) {
                onToggleShowPassword();
              } else {
                setInternalShowPassword((prev) => !prev);
              }
            }}
          >
            {showPassword ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        )}
      </div>
      {error && <span className="lovo-input__error" role="alert">{error.replace(/\.+$/, '')}</span>}
    </div>
  );
});
